// Homepage alert indicator for the staff Office Tasks scheduler. Shows an icon + count when there
// are open tasks (or any assigned to the caller). Neutral normally; attention (red) when there are
// overdue tasks. Click → /tasks, pre-filtered to "assigned to me" when the caller has assignments.
// Staff-only: gated on the shared canAccessRoute('/tasks') allow-list, so students/clients never see it.

import { useEffect, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { canAccessRoute } from '@avsuite/shared';
import { useAuth } from '../context/AuthContext';
import { fetchSummary, type OfficeTaskSummary } from '../lib/tasks';

export default function TasksHomeBadge() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState<OfficeTaskSummary | null>(null);

  const canSee = canAccessRoute('/tasks', user?.roles ?? []);

  useEffect(() => {
    if (!canSee) return;
    let alive = true;
    fetchSummary().then((d) => { if (alive) setSummary(d); });
    return () => { alive = false; };
  }, [canSee]);

  if (!canSee || !summary) return null;

  const show = summary.open > 0 || summary.assigned_to_me > 0;
  if (!show) return null;

  const attention = summary.overdue > 0;
  const noun = summary.open === 1 ? 'open task' : 'open tasks';
  const assignedPart = summary.assigned_to_me > 0 ? ` · ${summary.assigned_to_me} assigned to you` : '';
  const overduePart = attention ? ` · ${summary.overdue} overdue` : '';
  const label = `${summary.open} ${noun}${assignedPart}${overduePart}`;
  const target = summary.assigned_to_me > 0 ? '/tasks?assignee=me' : '/tasks';

  const cardStyle: CSSProperties = {
    display: 'flex', alignItems: 'center', gap: 12, width: '100%',
    padding: '14px 18px', marginTop: 16,
    background: '#1e293b', border: `1px solid ${attention ? '#7f1d1d' : '#334155'}`,
    borderRadius: 8, cursor: 'pointer', textAlign: 'left',
    color: '#e2e8f0', fontFamily: 'inherit', fontSize: 14,
  };
  const countBadge: CSSProperties = {
    minWidth: 22, height: 22, padding: '0 6px', borderRadius: 11,
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 12, fontWeight: 700,
    background: attention ? '#dc2626' : '#334155', color: '#fff',
  };

  return (
    <button type="button" onClick={() => navigate(target)} title={label} aria-label={label} style={cardStyle}>
      <span aria-hidden="true" style={{ fontSize: 18 }}>✅</span>
      <span style={{ fontWeight: 600 }}>Tasks</span>
      <span style={countBadge}>{summary.open > 99 ? '99+' : summary.open}</span>
      <span style={{ color: '#94a3b8', fontSize: 13 }}>
        {assignedPart ? `${summary.assigned_to_me} assigned to you` : 'open'}
        {attention && <span style={{ color: '#fca5a5', fontWeight: 600 }}>{` · ${summary.overdue} overdue`}</span>}
      </span>
      <span style={{ marginLeft: 'auto', color: '#7dd3fc', fontSize: 13 }}>→</span>
    </button>
  );
}
